import { validateDirectives } from '@insolitum/types';
import type {
  ActionsDirective,
  CalloutDirective,
  ChartDirective,
  ListDirective,
  MarkdownDirective,
  MetricDirective,
  RenderDirective,
  RenderTone,
  TableDirective,
  TimelineDirective,
} from '@insolitum/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// =============================================================================
// GenerativeSurface (Faza 4 — Jarvis) — renders AI render-directives.
//
// The AI proposes DATA (validated `RenderDirective`s); this component owns all
// presentation. It is intentionally DEPENDENCY-FREE: charts are hand-drawn SVG
// (no recharts/d3/visx — none are in the shell), and there is NO dangerous HTML
// (no `dangerouslySetInnerHTML`). Everything the AI sends is rendered as text or
// numbers inside elements we control, so a hostile payload cannot inject markup.
//
// Inputs are re-validated here via `validateDirectives` so the component is safe
// even if a caller forgets to validate (defence in depth).
// =============================================================================

// Tone colours come from the Aurora token layer (semantic status colours stay
// stable across brand/mode — see docs/design/AURORA_DS.md).
const TONE_COLORS: Record<RenderTone, { fg: string; bg: string; border: string }> = {
  neutral: {
    fg: 'var(--ids-text)',
    bg: 'var(--ids-bg-subtle)',
    border: 'var(--ids-border-strong)',
  },
  info: {
    fg: 'var(--ids-info)',
    bg: 'rgba(var(--ids-info-rgb),0.12)',
    border: 'rgba(var(--ids-info-rgb),0.40)',
  },
  success: {
    fg: 'var(--ids-success)',
    bg: 'rgba(var(--ids-success-rgb),0.12)',
    border: 'rgba(var(--ids-success-rgb),0.40)',
  },
  warning: {
    fg: 'var(--ids-warning)',
    bg: 'rgba(var(--ids-warning-rgb),0.14)',
    border: 'rgba(var(--ids-warning-rgb),0.40)',
  },
  danger: {
    fg: 'var(--ids-danger)',
    bg: 'rgba(var(--ids-danger-rgb),0.12)',
    border: 'rgba(var(--ids-danger-rgb),0.40)',
  },
};

// A small, deterministic palette for multi-series categorical colouring.
// Aurora tokens (SVG paint attributes accept var() — same pattern as the
// line/area chart's stroke="var(--ids-brand)" below); mapping per entry.
const SERIES_PALETTE = [
  'var(--ids-info)', // was #60a5fa — semantic info (blue)
  'var(--ids-success)', // was #34d399 — semantic success
  'var(--ids-warning)', // was #fbbf24 — semantic warning
  'var(--ids-danger)', // was #f87171 — semantic danger
  'var(--violet-400)', // was #a78bfa — Nexus violet (grad-c family; brand would collide with info)
  'var(--cyan-400)', // was #22d3ee — grad-a / info-alt cyan
  'var(--orange-400)', // was #fb923c — orange primitive (no --ids-warning-strong token)
];

// Every widget is a .jw tile inside the 12-col .jgrid (prototype: organism.css
// .jgrid/.jw + variants) — the container adds `.in` after mount for the
// staggered reveal (see components.css, ORGANIZM AI adaptacje).

// Widget titles are Aurora Jarvis-widget labels (prototype .jw__l).
const TITLE_CLASS = 'jw__l';

function tone(t?: RenderTone) {
  return TONE_COLORS[t ?? 'neutral'];
}

// -----------------------------------------------------------------------------
// metric
// -----------------------------------------------------------------------------
function Metric({ d }: { d: MetricDirective }) {
  const c = tone(d.tone);
  const arrow = d.trend === 'up' ? '▲' : d.trend === 'down' ? '▼' : d.trend === 'flat' ? '▬' : '';
  const trendClass =
    d.trend === 'up' ? 'trend trend--up' : d.trend === 'down' ? 'trend trend--down' : 'trend';
  return (
    <div className="jw jw--metric flex flex-col gap-0.5">
      <span className={TITLE_CLASS}>{d.label}</span>
      <span className="jw__v" style={{ color: c.fg }}>
        {arrow && (
          <span className={trendClass} style={{ fontSize: 14, marginRight: 6 }}>
            {arrow}
          </span>
        )}
        {d.value}
      </span>
      {d.sublabel && <span className="mt-1 text-[11px] text-aurora-subtle">{d.sublabel}</span>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// chart — hand-drawn SVG (bar / line / area / pie)
// -----------------------------------------------------------------------------
function Chart({ d }: { d: ChartDirective }) {
  const W = 320;
  const H = 160;
  const pad = 24;
  const values = d.points.map((p) => p.value);
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const innerW = W - pad * 2;
  const innerH = H - pad * 2;
  const fmt = (n: number) => `${n}${d.unit ? ` ${d.unit}` : ''}`;

  let body: React.ReactNode = null;

  if (d.variant === 'pie') {
    const total = values.reduce((s, v) => s + Math.max(0, v), 0) || 1;
    const cx = W / 2;
    const cy = H / 2;
    const r = Math.min(innerW, innerH) / 2;
    let acc = 0;
    body = (
      <g>
        {d.points.map((p, i) => {
          const frac = Math.max(0, p.value) / total;
          const start = acc * 2 * Math.PI;
          acc += frac;
          const end = acc * 2 * Math.PI;
          const large = end - start > Math.PI ? 1 : 0;
          const x1 = cx + r * Math.sin(start);
          const y1 = cy - r * Math.cos(start);
          const x2 = cx + r * Math.sin(end);
          const y2 = cy - r * Math.cos(end);
          const color = SERIES_PALETTE[i % SERIES_PALETTE.length];
          return (
            <path
              key={i}
              d={`M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`}
              fill={color}
              opacity={0.85}
            >
              <title>{`${p.label}: ${fmt(p.value)}`}</title>
            </path>
          );
        })}
      </g>
    );
  } else if (d.variant === 'bar') {
    const bw = innerW / d.points.length;
    body = (
      <g>
        {d.points.map((p, i) => {
          const h = (Math.max(0, p.value - min) / span) * innerH;
          const x = pad + i * bw + bw * 0.15;
          const y = H - pad - h;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={bw * 0.7}
              height={Math.max(0, h)}
              rx={2}
              fill={SERIES_PALETTE[i % SERIES_PALETTE.length]}
              opacity={0.85}
            >
              <title>{`${p.label}: ${fmt(p.value)}`}</title>
            </rect>
          );
        })}
      </g>
    );
  } else {
    // line / area
    const step = d.points.length > 1 ? innerW / (d.points.length - 1) : 0;
    const pts = d.points.map((p, i) => {
      const x = pad + i * step;
      const y = H - pad - (Math.max(0, p.value - min) / span) * innerH;
      return { x, y, p };
    });
    const path = pts
      .map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`)
      .join(' ');
    const areaPath =
      d.variant === 'area' && pts.length
        ? `${path} L ${pts[pts.length - 1].x.toFixed(1)} ${H - pad} L ${pts[0].x.toFixed(1)} ${H - pad} Z`
        : '';
    body = (
      <g>
        {areaPath && <path d={areaPath} fill="var(--ids-brand)" opacity={0.18} />}
        <path d={path} fill="none" stroke="var(--ids-brand)" strokeWidth={2} />
        {pts.map((pt, i) => (
          <circle key={i} cx={pt.x} cy={pt.y} r={2.5} fill="var(--ids-brand)">
            <title>{`${pt.p.label}: ${fmt(pt.p.value)}`}</title>
          </circle>
        ))}
      </g>
    );
  }

  return (
    <div className="jw jw--chart">
      {d.title && <p className={TITLE_CLASS}>{d.title}</p>}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        // .jchart = animowane kreślenie linii po wejściu kafla (prototyp:
        // organism.css .jchart path + drawline); tylko warianty liniowe.
        className={d.variant === 'line' || d.variant === 'area' ? 'jchart' : undefined}
        style={{ display: 'block', maxWidth: W }}
        role="img"
        aria-label={d.title || `${d.variant} chart`}
      >
        {body}
      </svg>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {d.points.map((p, i) => (
          <span key={i} className="inline-flex gap-1 text-[10px] text-aurora-subtle">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: SERIES_PALETTE[i % SERIES_PALETTE.length] }}
            />
            {p.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// table
// -----------------------------------------------------------------------------
function Table({ d }: { d: TableDirective }) {
  return (
    <div className="jw jw--table">
      {d.title && <p className={TITLE_CLASS}>{d.title}</p>}
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              {d.columns.map((col, i) => (
                <th key={i} className="whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// timeline
// -----------------------------------------------------------------------------
function Timeline({ d }: { d: TimelineDirective }) {
  return (
    <div className="jw jw--span6">
      {d.title && <p className={TITLE_CLASS}>{d.title}</p>}
      <ol className="m-0 list-none p-0">
        {d.items.map((it, i) => {
          const c = tone(it.tone);
          return (
            <li key={i} className="flex gap-2.5 py-1">
              <span
                className="mt-[5px] h-2 w-2 flex-none rounded-full"
                style={{ background: c.fg }}
              />
              <div className="min-w-0">
                <div className="mono text-[11px] text-aurora-subtle">{it.at}</div>
                <div className="text-[13px] font-semibold text-aurora-text">{it.title}</div>
                {it.description && (
                  <div className="text-xs text-aurora-muted">{it.description}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// -----------------------------------------------------------------------------
// list
// -----------------------------------------------------------------------------
function ListWidget({ d }: { d: ListDirective }) {
  const Tag = d.ordered ? 'ol' : 'ul';
  return (
    <div className="jw jw--list">
      {d.title && <p className={TITLE_CLASS}>{d.title}</p>}
      <Tag className="m-0 pl-[18px] text-[13px] text-aurora-text">
        {d.items.map((it, i) => (
          <li key={i} className="py-0.5">
            {it}
          </li>
        ))}
      </Tag>
    </div>
  );
}

// -----------------------------------------------------------------------------
// callout
// -----------------------------------------------------------------------------
function Callout({ d }: { d: CalloutDirective }) {
  const c = tone(d.tone);
  // .jw--callout defaults to the prototype's warning tint; the inline tone
  // style keeps the semantic tint 1:1 for the other tones (info/success/…).
  return (
    <div
      className="jw jw--callout flex-col gap-1"
      style={{ background: c.bg, borderColor: c.border }}
    >
      {d.title && (
        <p className={`${TITLE_CLASS} mb-1`} style={{ color: c.fg }}>
          {d.title}
        </p>
      )}
      <p className="m-0 text-[13px] text-aurora-text">{d.text}</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// actions — each button dispatched via onAction; confirm-gating is the caller's
// responsibility (we surface `requiresConfirm` so the caller can prompt).
// -----------------------------------------------------------------------------
function Actions({
  d,
  onAction,
}: {
  d: ActionsDirective;
  onAction?: (action: string, params?: Record<string, unknown>, requiresConfirm?: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="jw jw--actions">
      {d.title && <p className={TITLE_CLASS}>{d.title}</p>}
      <div className="flex flex-wrap gap-2">
        {d.actions.map((a, i) => {
          // Prototype (organism.js:333): confirm-gated actions are secondary
          // buttons with a warning "✋ potwierdź" pill; plain actions are tonal.
          // The semantic tone tint stays only for danger/success variants.
          const c = a.tone === 'danger' || a.tone === 'success' ? tone(a.tone) : undefined;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onAction?.(a.action, a.params, a.requiresConfirm)}
              className={`btn ${a.requiresConfirm ? 'btn--secondary' : 'btn--tonal'} btn--sm cursor-pointer`}
              style={c ? { background: c.bg, color: c.fg, borderColor: c.border } : undefined}
            >
              {a.label}
              {a.requiresConfirm && (
                <span className="jw__confirm">✋ {t('nexus.confirmAction', 'potwierdź')}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// markdown — SAFE restricted subset rendered as plain React nodes (NO HTML
// injection). Supports paragraphs, `- ` bullets, and **bold** inline spans.
// -----------------------------------------------------------------------------
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  // Split on **bold** while keeping the delimiters' content.
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>;
    }
    return <span key={`${keyPrefix}-${i}`}>{part}</span>;
  });
}

function Markdown({ d }: { d: MarkdownDirective }) {
  const lines = d.text.split('\n');
  return (
    <div className="jw jw--span6 text-[13px] leading-normal text-aurora-text">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-1.5" />;
        if (trimmed.startsWith('- ')) {
          return (
            <div key={i} className="flex gap-1.5">
              <span>•</span>
              <span>{renderInline(trimmed.slice(2), `md-${i}`)}</span>
            </div>
          );
        }
        return (
          <p key={i} className="my-0.5">
            {renderInline(trimmed, `md-${i}`)}
          </p>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// dispatcher
// -----------------------------------------------------------------------------
function DirectiveView({
  d,
  onAction,
}: {
  d: RenderDirective;
  onAction?: (action: string, params?: Record<string, unknown>, requiresConfirm?: boolean) => void;
}) {
  switch (d.kind) {
    case 'metric':
      return <Metric d={d} />;
    case 'chart':
      return <Chart d={d} />;
    case 'table':
      return <Table d={d} />;
    case 'timeline':
      return <Timeline d={d} />;
    case 'list':
      return <ListWidget d={d} />;
    case 'callout':
      return <Callout d={d} />;
    case 'actions':
      return <Actions d={d} onAction={onAction} />;
    case 'markdown':
      return <Markdown d={d} />;
    default:
      return null;
  }
}

export interface GenerativeSurfaceProps {
  /** Raw or pre-validated directives. Always re-validated (defence in depth). */
  directives: unknown;
  /** Called when an action button is pressed. Caller owns confirm-gating. */
  onAction?: (action: string, params?: Record<string, unknown>, requiresConfirm?: boolean) => void;
}

/**
 * Render a list of validated render-directives into native widgets. Invalid /
 * unknown directives are dropped by `validateDirectives`; an empty input
 * renders nothing (returns null).
 */
export function GenerativeSurface({ directives, onAction }: GenerativeSurfaceProps) {
  const valid = validateDirectives(directives);
  // Staggered reveal (prototype: organism.js jarvisRun) — the container gains
  // `.in` right after mount and CSS (.jgrid.in > .jw + nth-child delays)
  // uncovers the tiles step by step. Purely presentational; with
  // prefers-reduced-motion the CSS shows everything immediately.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  if (valid.length === 0) return null;
  return (
    <div data-testid="generative-surface" className={`jgrid mt-2.5${entered ? ' in' : ''}`}>
      {valid.map((d, i) => (
        <DirectiveView key={i} d={d} onAction={onAction} />
      ))}
    </div>
  );
}

export default GenerativeSurface;
